/**
 * Presents the supported connector cadences without exposing scheduler syntax.
 *
 * Feeds persist a product-level schedule; translating it in one control keeps
 * add, edit, and connected-source views consistent.
 */
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import type { ConnectorSchedule } from '@/subsystems/vault/connectors/types'

export const connectorScheduleLabels: Record<ConnectorSchedule, string> = {
  manual: 'Off',
  'every-minute': 'Every minute (sandbox/demo)',
  'every-5-minutes': 'Every 5 minutes',
  'every-30-minutes': 'Every 30 minutes',
  hourly: 'Hourly',
  'every-6-hours': 'Every 6 hours',
  daily: 'Daily',
}

export function ConnectorScheduleSelect({
  value,
  onChange,
  disabled = false,
  ariaLabel = 'Connector schedule',
  className,
}: {
  value: ConnectorSchedule
  onChange: (value: ConnectorSchedule) => void
  disabled?: boolean
  ariaLabel?: string
  className?: string
}) {
  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={next => onChange(next as ConnectorSchedule)}
    >
      <SelectTrigger className={className} aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(Object.entries(connectorScheduleLabels) as [ConnectorSchedule, string][]).map(
          ([schedule, label]) => (
            <SelectItem key={schedule} value={schedule}>{label}</SelectItem>
          ),
        )}
      </SelectContent>
    </Select>
  )
}

export function ConnectorScheduleField({
  value,
  onChange,
  ariaLabel,
  disabled = false,
}: {
  value: ConnectorSchedule
  onChange: (value: ConnectorSchedule) => void
  ariaLabel: string
  disabled?: boolean
}) {
  return (
    <div className="space-y-2">
      <Label>Run automatically</Label>
      <ConnectorScheduleSelect
        value={value}
        onChange={onChange}
        disabled={disabled}
        ariaLabel={ariaLabel}
        className="w-full sm:w-72"
      />
      <p className="text-xs text-muted-foreground">
        Every minute is intended only for sandbox demonstrations.
      </p>
    </div>
  )
}
